import { FloatLabel } from "primereact/floatlabel";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { InputTextarea } from "primereact/inputtextarea";
import { InputSwitch } from "primereact/inputswitch";
import { Button } from "primereact/button";
import { useCallback, useEffect, useState } from "react";
import { getReadableDate, getWriteableDate, toAppDate } from "../../../utils";
import HasRequiredAuthority from "../../dependencies/HasRequiredAuthority";
import TabHeader from "../../common/TabHeader";
import { Divider } from "primereact/divider";
import { useAppContext } from "../../../providers/ProviderAppContainer";
import Loading from "../../common/Loading";
import NoContent from "../../common/NoContent";
import { AUTHORITIES } from "../../../constants";
import { useOutletContext } from "react-router-dom";
import { TEXT_NORMAL } from "../../../style";
import FileInput from "../../common/FileInput";
import { InputNumber } from "primereact/inputnumber";
import { Calendar } from "primereact/calendar";
import { Accordion, AccordionTab } from "primereact/accordion";
import CheckboxInput from "../../common/CheckBoxInput";

export default function Basics() {
    const { userId, branches } = useOutletContext();
    const [basics, setBasics] = useState();

    const { requestAPI, showToast } = useAppContext();

    const [loading, setLoading] = useState();
    const [error, setError] = useState();

    const [updating, setUpdating] = useState();

    const [enableInputs, setEnableInputs] = useState();

    useEffect(() => {
        requestAPI({
            requestPath: `users/${userId}`,
            requestMethod: "GET",
            setLoading: setLoading,
            onRequestFailure: setError,
            onResponseReceieved: (loadedBasics, responseCode) => {
                if (loadedBasics && responseCode === 200) {
                    setBasics({ ...loadedBasics, dob: toAppDate(loadedBasics?.dob) });
                } else {
                    setError("Couldn't load User Profile");
                }
            },
        });
    }, [requestAPI, userId]);

    const updateUserBasics = useCallback(() => {
        requestAPI({
            requestPath: `users`,
            requestMethod: "PUT",
            requestPostBody: {
                ...basics,
                dob: basics?.dob instanceof Date ? getWriteableDate({ date: basics.dob, removeTime: true }) : basics?.dob || null,
            },
            setLoading: setUpdating,
            onRequestFailure: setError,
            onResponseReceieved: (updatedBasics, responseCode) => {
                if (updatedBasics && responseCode === 200) {
                    showToast({ severity: "success", summary: "Updated", detail: "Profile Updated Succesfully", life: 1000 });
                    setBasics({ ...updatedBasics, dob: toAppDate(updatedBasics?.dob) });
                } else {
                    showToast({ severity: "error", summary: "Failed", detail: "Failed To Updated Profile !", life: 2000 });
                }
            },
        });
    }, [basics, requestAPI, showToast]);

    return (
        <div className="flex flex-column h-full ">
            <TabHeader
                className={"px-3 pt-3"}
                title="User's Basic Details & Profile"
                highlights={[`Created At - ${getReadableDate({ date: basics?.created_on })}`, `Updated At - ${getReadableDate({ date: basics?.updated_at })}`]}
                actionItems={[
                    <HasRequiredAuthority requiredAuthority={AUTHORITIES.MANAGE_OTHER_USERS}>
                        <InputSwitch
                            checked={Boolean(basics?.active)}
                            onChange={(e) => setBasics((prev) => ({ ...prev, active: e.value }))}
                            disabled={!enableInputs}
                        />
                    </HasRequiredAuthority>,
                    <HasRequiredAuthority requiredAuthority={AUTHORITIES.UPDATE_USER}>
                        <Button icon="pi pi-pencil" severity="warning" onClick={setEnableInputs} />
                    </HasRequiredAuthority>,
                ]}
            />
            <Divider />

            {loading ? (
                <Loading message="Loading Inquiries" />
            ) : error ? (
                <NoContent error={error} />
            ) : basics ? (
                <div className="flex-1 px-2 flex flex-column gap-2 overflow-y-scroll ">
                    <FileInput
                        label="User Image"
                        type="image"
                        cdn_url={basics?.image}
                        setCDNUrl={(cdn_url) => setBasics((prev) => ({ ...prev, image: cdn_url }))}
                        disabled={loading || !enableInputs}
                        source_accessible={false}
                    />
                    <FloatLabel className="mt-4">
                        <InputText
                            value={basics?.full_name || ""}
                            id="fullname"
                            className="w-full"
                            onChange={(e) => setBasics((prev) => ({ ...prev, full_name: e.target.value }))}
                            disabled={!enableInputs}
                            pt={{
                                root: { className: TEXT_NORMAL },
                            }}
                        />
                        <label htmlFor="fullname">Full Name</label>
                    </FloatLabel>
                    <FloatLabel className="mt-4">
                        <InputText
                            value={basics?.email || ""}
                            id="email"
                            className="w-full"
                            onChange={(e) => setBasics((prev) => ({ ...prev, email: e.target.value }))}
                            disabled={!enableInputs}
                        />
                        <label htmlFor="email">Email</label>
                    </FloatLabel>
                    <div className="flex mt-4 gap-2 items-center">
                        <FloatLabel className="flex-1">
                            <InputNumber
                                useGrouping={false}
                                className="w-full"
                                value={basics?.phone || ""}
                                id="phone"
                                onChange={(e) => setBasics((prev) => ({ ...prev, phone: e.value }))}
                                disabled={!enableInputs}
                                pt={{
                                    root: { className: TEXT_NORMAL },
                                }}
                            />
                            <label htmlFor="phone">Phone</label>
                        </FloatLabel>

                        <FloatLabel className="flex-1">
                            <Dropdown
                                className="w-full"
                                value={branches?.find(({ id }) => id === basics?.branch_id)}
                                inputId="branch"
                                options={branches}
                                optionLabel="title"
                                onChange={(e) => setBasics((prev) => ({ ...prev, branch_id: e.value?.id }))}
                                disabled={!enableInputs}
                                pt={{
                                    input: { className: TEXT_NORMAL },
                                    item: { className: TEXT_NORMAL },
                                }}
                            />
                            <label htmlFor="branch">Branch</label>
                        </FloatLabel>
                    </div>

                    <FloatLabel className="mt-4">
                        <InputNumber
                            useGrouping={false}
                            className="w-full"
                            value={basics?.guardian_phone || ""}
                            id="guardian_phone"
                            onChange={(e) => setBasics((prev) => ({ ...prev, guardian_phone: e.value }))}
                            disabled={!enableInputs}
                            pt={{
                                root: { className: TEXT_NORMAL },
                            }}
                        />
                        <label htmlFor="guardian_phone">Guardian Phone</label>
                    </FloatLabel>

                    <FloatLabel className="mt-4">
                        <InputText
                            value={basics?.guardian_email || ""}
                            id="guardian_email"
                            className="w-full"
                            onChange={(e) => setBasics((prev) => ({ ...prev, guardian_email: e.target.value }))}
                            disabled={!enableInputs}
                            pt={{
                                root: { className: TEXT_NORMAL },
                            }}
                        />
                        <label htmlFor="guardian_email">Guardian Email</label>
                    </FloatLabel>

                    <FloatLabel className="mt-4">
                        <Calendar
                            dateFormat="dd/mm/yy"
                            inputId="dob"
                            className="w-full"
                            value={basics?.dob instanceof Date ? basics.dob : toAppDate(basics?.dob)}
                            onChange={(e) => setBasics((prev) => ({ ...prev, dob: e.value }))}
                            disabled={!enableInputs}
                            showIcon
                            maxDate={new Date()}
                        />
                        <label htmlFor="dob">Date of Birth</label>
                    </FloatLabel>

                    <FloatLabel className="mt-4">
                        <InputTextarea
                            value={basics?.address || ""}
                            id="address"
                            rows={5}
                            cols={30}
                            className="w-full"
                            onChange={(e) => setBasics((prev) => ({ ...prev, address: e.target.value }))}
                            disabled={!enableInputs}
                            pt={{
                                root: { className: TEXT_NORMAL },
                            }}
                        />
                        <label htmlFor="address">Address</label>
                    </FloatLabel>

                    <CheckboxInput
                        disabled={!enableInputs}
                        label={"Allow Psychometric Test "}
                        checked={!!basics?.stream_selection_test_allowed}
                        onChange={(checked) => setBasics((prev) => ({ ...prev, stream_selection_test_allowed: checked }))}
                    />

                    <Accordion>
                        <AccordionTab header="History">
                            <FloatLabel className="mt-2">
                                <InputText
                                    value={basics?.history?.institute}
                                    id="institute"
                                    className="w-full"
                                    onChange={(e) => setBasics((prev) => ({ ...prev, history: { ...prev?.history, institute: e.target.value } }))}
                                    disabled={!enableInputs}
                                />
                                <label htmlFor="institute">Last College</label>
                            </FloatLabel>
                            <FloatLabel className="mt-4">
                                <InputText
                                    value={basics?.history?.course}
                                    id="course"
                                    className="w-full"
                                    onChange={(e) => setBasics((prev) => ({ ...prev, history: { ...prev?.history, course: e.target.value } }))}
                                    disabled={!enableInputs}
                                />
                                <label htmlFor="class">Last Class</label>
                            </FloatLabel>
                            <FloatLabel className="mt-4">
                                <InputText
                                    value={basics?.history?.refered_by}
                                    id="refered_by"
                                    className="w-full"
                                    onChange={(e) => setBasics((prev) => ({ ...prev, history: { ...prev?.history, refered_by: e.target.value } }))}
                                    disabled={!enableInputs}
                                />
                                <label htmlFor="refered_by">Refered By</label>
                            </FloatLabel>
                        </AccordionTab>
                    </Accordion>
                </div>
            ) : (
                <NoContent error={"No Inquiries Found"} />
            )}

            <HasRequiredAuthority requiredAuthority={AUTHORITIES.UPDATE_USER}>
                <Button
                    className="mx-3 my-2"
                    label="Update"
                    severity="warning"
                    onClick={updateUserBasics}
                    loading={updating}
                    disabled={!basics}
                    pt={{
                        label: { className: TEXT_NORMAL },
                        icon: { className: TEXT_NORMAL },
                    }}
                />
            </HasRequiredAuthority>
        </div>
    );
}
